import { useEffect, useMemo, useRef, useState } from "react";
import { animate, motion } from "framer-motion";
import { ArrowRight, Check, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadEstimatePdf, fmtLakh } from "@/lib/estimatePdf";
import { COST_GUIDE } from "@/data/site";
import { Reveal, Eyebrow } from "./Reveal";
import { BlueprintGrid } from "./Blueprint";
import { EASE } from "./motion";


// Animated rupee figure that glides to each new value.
const Money = ({ lakh, testId }) => {
  const [v, setV] = useState(lakh);
  const prev = useRef(lakh);
  useEffect(() => {
    const c = animate(prev.current, lakh, { duration: 0.6, ease: EASE, onUpdate: setV });
    prev.current = lakh;
    return () => c.stop();
  }, [lakh]);
  return <span className="tabular-nums" data-testid={testId}>{fmtLakh(v)}</span>;
};

export const CostGuide = () => {
  const { areaMin, areaMax, areaStep, defaultArea, finishes, extras } = COST_GUIDE;
  const [area, setArea] = useState(defaultArea);
  const [finish, setFinish] = useState(finishes[1].id);
  const [picked, setPicked] = useState([]);

  const { low, high, perSqft } = useMemo(() => {
    const f = finishes.find((x) => x.id === finish);
    const ex = extras.filter((e) => picked.includes(e.id));
    const lo = f.rate[0] + ex.reduce((s, e) => s + e.rate[0], 0);
    const hi = f.rate[1] + ex.reduce((s, e) => s + e.rate[1], 0);
    return { low: (lo * area) / 1e5, high: (hi * area) / 1e5, perSqft: [lo, hi] };
  }, [area, finish, picked, finishes, extras]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const pct = ((area - areaMin) / (areaMax - areaMin)) * 100;
  const summary = `Renovation estimate: ${area} sq ft, ${finishes.find((f) => f.id === finish).label} finish${picked.length ? ` + ${extras.filter((e) => picked.includes(e.id)).map((e) => e.label).join(", ")}` : ""} — roughly ${fmtLakh(low)} to ${fmtLakh(high)}.`;
  const discuss = () => sessionStorage.setItem("carvings:prefill", JSON.stringify({ project_type: "Residential", message: `${summary}\n\nSite location: \nTimeline: ` }));
  const [pdfBusy, setPdfBusy] = useState(false);
  const download = async () => {
    setPdfBusy(true);
    try {
      await downloadEstimatePdf({ area, finish: finishes.find((f) => f.id === finish), extras: extras.filter((e) => picked.includes(e.id)), low, high, perSqft });
      toast.success("Your estimate PDF is downloading.");
    } catch (err) {
      toast.error("Could not build the PDF. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <section id="cost-guide" className="relative bg-[#FBFBF9] py-24 lg:py-36 overflow-hidden" data-testid="cost-guide-section">
      <BlueprintGrid className="opacity-70" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-7">
          <Eyebrow className="mb-6">Renovation cost guide</Eyebrow>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-stone-900 leading-tight">What might your renovation cost?</h2>
          <p className="mt-5 max-w-lg text-stone-600 font-light leading-relaxed">Pick a size and finish to see a rough budget. Every home is different — treat this as a starting point for our first conversation.</p>

          <div className="mt-12">
            <div className="flex items-end justify-between">
              <label htmlFor="area-range" className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Built-up area</label>
              <p className="font-serif text-3xl text-stone-900 tabular-nums" data-testid="cost-area-value">{area.toLocaleString("en-IN")} <span className="font-mono text-xs tracking-[0.2em] text-stone-500">SQ FT</span></p>
            </div>
            <div className="relative mt-4 tape-range">
              <div className="tape-ticks" aria-hidden="true" />
              <motion.div className="tape-fill" style={{ width: `${pct}%` }} aria-hidden="true" />
              <input id="area-range" type="range" min={areaMin} max={areaMax} step={areaStep} value={area} onChange={(e) => setArea(Number(e.target.value))} aria-valuetext={`${area} square feet`} data-testid="cost-area-range" />
            </div>
            <div className="mt-2 flex justify-between font-mono text-[10px] tracking-[0.2em] text-stone-400"><span>{areaMin} SQ FT</span><span>{areaMax.toLocaleString("en-IN")} SQ FT</span></div>
          </div>

          <div className="mt-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Finish level</p>
            <div className="mt-4 grid sm:grid-cols-3 gap-3" role="radiogroup" data-testid="cost-finish-group">
              {finishes.map((f) => (
                <motion.button key={f.id} type="button" role="radio" aria-checked={finish === f.id} onClick={() => setFinish(f.id)} whileTap={{ scale: 0.97 }} data-testid={`cost-finish-${f.id}`}
                  className={`relative text-left p-5 border transition-colors duration-300 ${finish === f.id ? "border-stone-900 bg-white" : "border-stone-300 hover:border-stone-500"}`}>
                  {finish === f.id && <motion.span layoutId="finish-mark" className="absolute top-3 right-3 w-5 h-5 bg-stone-900 text-[#FBFBF9] flex items-center justify-center" transition={{ type: "spring", stiffness: 400, damping: 30 }}><Check size={12} /></motion.span>}
                  <p className="font-serif text-xl text-stone-900">{f.label}</p>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-[#B85C38]">₹{f.rate[0].toLocaleString("en-IN")}–{f.rate[1].toLocaleString("en-IN")} / SQ FT</p>
                  <p className="mt-3 text-xs text-stone-600 font-light leading-relaxed">{f.text}</p>
                </motion.button>
              ))}
            </div>
          </div>

          <div className="mt-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Add to the scope</p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="cost-extras-group">
              {extras.map((e) => {
                const on = picked.includes(e.id);
                return (
                  <motion.button key={e.id} type="button" aria-pressed={on} onClick={() => toggle(e.id)} whileTap={{ scale: 0.96 }} data-testid={`cost-extra-${e.id}`}
                    className={`px-4 py-2.5 border font-mono text-xs uppercase tracking-[0.12em] transition-colors duration-300 ${on ? "bg-stone-900 text-[#FBFBF9] border-stone-900" : "border-stone-300 text-stone-700 hover:border-stone-900"}`}>
                    {on ? "— " : "+ "}{e.label}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} variant="rise" className="lg:col-span-5">
          <div className="lg:sticky lg:top-24 bg-[#161614] text-[#FBFBF9] p-8 lg:p-10 relative overflow-hidden" data-testid="cost-result-card">
            <BlueprintGrid dark />
            <div className="relative">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#C29B38]">Rough budget</p>
              <p className="mt-6 font-serif font-light text-4xl sm:text-5xl leading-none tracking-tight">
                <Money lakh={low} testId="cost-low" /> <span className="text-stone-500">—</span> <Money lakh={high} testId="cost-high" />
              </p>
              <p className="mt-4 font-mono text-[11px] tracking-[0.15em] text-stone-400" data-testid="cost-rate">₹{perSqft[0].toLocaleString("en-IN")}–{perSqft[1].toLocaleString("en-IN")} per sq ft · {area.toLocaleString("en-IN")} sq ft</p>
              <dl className="mt-8 border-t border-stone-700 divide-y divide-stone-700 font-mono text-xs">
                {[["Finish", finishes.find((f) => f.id === finish).label], ["Extras", picked.length ? extras.filter((e) => picked.includes(e.id)).map((e) => e.label).join(", ") : "None"], ["Design fee", "Included in discussion"]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-6 py-3"><dt className="text-stone-500 uppercase tracking-wider shrink-0">{k}</dt><dd className="text-right text-stone-200">{v}</dd></div>
                ))}
              </dl>
              <div className="mt-8 flex flex-wrap gap-3">
                <motion.a whileTap={{ scale: 0.97 }} href="/contact" onClick={discuss} className="btn-dark !bg-[#FBFBF9] !text-stone-900 !border-[#FBFBF9] hover:!bg-[#C29B38] hover:!border-[#C29B38]" data-testid="cost-discuss-btn">Discuss this estimate <ArrowRight size={16} /></motion.a>
                <motion.button whileTap={{ scale: 0.97 }} type="button" onClick={download} disabled={pdfBusy} className="btn-ghost !border-stone-500 !text-[#FBFBF9] hover:!border-[#FBFBF9] disabled:opacity-60" data-testid="cost-download-btn">
                  {pdfBusy ? <span className="blocks" aria-label="Preparing PDF"><i /><i /><i /></span> : <>Download PDF <Download size={16} /></>}
                </motion.button>
              </div>
              <p className="mt-6 text-[11px] leading-relaxed text-stone-500">Indicative range for Kerala, excluding land, approvals and GST. A site visit gives you a proper quote.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
